import React from "react";
// @ts-ignore - React 18 types not yet installed, but runtime works correctly
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import "@amzn/awsui-global-styles/polaris.css";
import App from "./App";
import { loadAppConfig } from "./config/config";
import { initAuth } from "@amzn/atc-authorization-static-website";
import store from "./redux/store";
import { setUserInfo, clearUserInfo } from "./redux/actions/userActions";
import { pingRequest } from "./redux/actions/apiActions";
import { isPortalHost, isGohHost, buildPortalUrl, PORTAL_CARD_CLICK, getCurrentStage, PORTAL_ROOT, isLocalHost, resolveDomain } from "./config/domain";
import { PORTAL_NAVIGATION_TITLE, getSystemByKey } from "./config/systems";
import { redirectToLoginPage } from "./utils/auth-provider";

// Suppress the benign "ResizeObserver loop" error. This is a well-known,
// harmless browser notification (the observer simply couldn't deliver all
// callbacks within a single frame) that the webpack dev overlay otherwise
// surfaces as a full-screen runtime error. It is emitted by Cloudscape/Polaris
// components (tables, grids) and does not indicate a real problem.
const RESIZE_OBSERVER_LOOP_MESSAGES = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
];
window.addEventListener("error", (e) => {
  if (e.message && RESIZE_OBSERVER_LOOP_MESSAGES.some((m) => e.message.includes(m))) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Enable Polaris visual refresh for modern AWS UI design
document.body.classList.add("awsui-visual-refresh");
document.body.classList.add("awsui-polaris-light-mode");

const domainInfo = resolveDomain();
if (domainInfo.isPortal) {
  document.title = PORTAL_NAVIGATION_TITLE;
} else {
  const sys = getSystemByKey(domainInfo.system as string);
  if (sys) {
    document.title = `${sys.label} System Web Application`;
  }
}

function clearCardClickCookie() {
  const hostname = window.location.hostname;
  const isLocal = isLocalHost(hostname);
  const stage = getCurrentStage();
  const cookieDomain = isLocal ? hostname : (stage === "prod" ? `.${PORTAL_ROOT}` : `.${stage}.${PORTAL_ROOT}`);
  document.cookie = `${PORTAL_CARD_CLICK}=;domain=${cookieDomain};path=/;max-age=0`;
}

// Load configuration and initialize auth
loadAppConfig()
  .then(async ({ config }) => {
    console.log("[Init] Configuration loaded, initializing auth...");

    // The portal host (goh.lab126.amazon.dev, no system prefix) is a public
    // landing page: it must render WITHOUT forcing a login. System hosts
    // (msa.*, atlas.*) keep the default behavior of redirecting to login when
    // unauthenticated. We still run initAuth on the portal so that a user who
    // is already signed in (e.g. returning from a system) is recognized.
    const portal = isPortalHost();

    await initAuth(config, {
      // Only auto-redirect to login on system hosts. On the portal, leave the
      // user unauthenticated and let them click "Log in" explicitly.
      autoRedirectToLogin: false,
      onAuthenticated: (user, tokenInfo) => {
        console.log("[Init] User authenticated:", user.username);

        store.dispatch(
          setUserInfo({
            username: user.username,
            email: user.email,
            familyName: user.familyName,
            givenName: user.givenName,
            provider: user.provider,
            idToken: tokenInfo.token,
            accessToken: tokenInfo.accessToken,
            refreshToken: tokenInfo.refreshToken,
            tokenExpiration: tokenInfo.expireTime,
            baseUrl: (config as any).BASE_URL || '',
          }),
        );

        clearCardClickCookie();
        store.dispatch(pingRequest());
        renderApp();
      },
      onUnauthenticated: () => {
        store.dispatch(clearUserInfo());
        if (portal) {
          renderApp();
        } else {
          const fromCard = document.cookie.split(";").some((c) => c.trim().startsWith(PORTAL_CARD_CLICK + "="));
          if (fromCard) {
            clearCardClickCookie();
            redirectToLoginPage();
          } else if (isGohHost()) {
            window.location.href = buildPortalUrl();
          } else {
            redirectToLoginPage();
          }
        }
      },
      onTokenRefreshed: (token, expiration) => {
        store.dispatch(
          setUserInfo({
            ...store.getState().user.userInfo,
            username: store.getState().user.userName,
            email: store.getState().user.email,
            idToken: token,
            tokenExpiration: expiration,
          }),
        );
      },
      onSessionExpired: () => {
        // Let TokenManager handle refresh via getAccessToken() in the saga.
        // If refreshToken is still valid, it will silently renew; otherwise
        // the saga redirects to login when getAccessToken() returns null.
      },
    });
  })
  .catch((error) => {
    console.error("[Init] Failed to load configuration:", error);
    renderApp();
  });

function renderApp() {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>,
  );
}
